import React from "react";
import PollIntervalContext from "./PollIntervalContext";

export interface PollIntervalProviderProps {
  /** Interval in milliseconds between ticks. Defaults to 30 000 ms (30 s). */
  intervalMs?: number;
  children: React.ReactNode;
}

/**
 * Runs a single shared `setInterval` and notifies all subscribers on each tick.
 *
 * Place this provider high in the tree (e.g. alongside `SnackPackProvider` in
 * `index.tsx`) so that any hook in the subtree can subscribe via
 * {@link usePollInterval} without creating its own timer.
 *
 * Multiple hooks subscribing to the same provider share one timer — no matter
 * how many callers, there is exactly one network-tick per interval.
 *
 * @example
 * ```tsx
 * <PollIntervalProvider intervalMs={30_000}>
 *   <App />
 * </PollIntervalProvider>
 * ```
 */
export const PollIntervalProvider: React.FC<PollIntervalProviderProps> = ({ intervalMs = 30_000, children }) => {
  const subscribers = React.useRef(new Set<() => void>());

  React.useEffect(() => {
    const id = setInterval(() => {
      subscribers.current.forEach((cb) => cb());
    }, intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);

  const subscribe = React.useCallback((callback: () => void) => {
    subscribers.current.add(callback);
    return () => {
      subscribers.current.delete(callback);
    };
  }, []);

  const value = React.useMemo(() => ({ subscribe }), [subscribe]);

  return <PollIntervalContext.Provider value={value}>{children}</PollIntervalContext.Provider>;
};
